import React from "react";
import { CheckCircle2, XCircle, AlertCircle } from "lucide-react";
import type { SendBulkEmailResult } from "src/types/api/vendor/postVendorsBulkEmail";

interface SuccessProps {
  result: SendBulkEmailResult;
  onClose: () => void;
}

interface ErrorProps {
  onRetry: () => void;
  onClose: () => void;
}

const Success: React.FC<SuccessProps> = ({ result, onClose }) => {
  const failed = result.results.filter((r) => !r.sent);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="w-full max-w-md overflow-hidden rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50 p-5">
          <div className="flex items-center space-x-3">
            <CheckCircle2 className="h-6 w-6 text-success-600" />
            <h2 className="text-lg font-semibold text-gray-900">Bulk email complete</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-2xl leading-none text-gray-400 hover:text-gray-600"
            aria-label="Close"
          >
            &times;
          </button>
        </div>

        <div className="space-y-4 p-5">
          <div className="rounded-lg border border-success-200 bg-success-50 p-4">
            <p className="text-sm text-gray-700">
              <span className="font-semibold text-success-700">{result.sentCount}</span> email(s) sent
              {result.skippedCount > 0 && (
                <>
                  , <span className="font-semibold text-success-700">{result.skippedCount}</span> skipped
                </>
              )}
              .
            </p>
          </div>

          {failed.length > 0 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
              <p className="mb-2 text-sm font-medium text-amber-800">Skipped or failed</p>
              <ul className="max-h-40 space-y-1 overflow-y-auto text-sm text-amber-800">
                {failed.map((r) => (
                  <li key={r.vendorId || r.vendorName} className="flex items-start gap-2">
                    <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                    <span>
                      {r.vendorName || r.vendorId}: {r.error ?? "Not sent"}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex justify-center pt-2">
            <button
              type="button"
              className="rounded-md bg-black px-6 py-2 text-sm font-medium text-white transition-colors hover:bg-gray-800"
              onClick={onClose}
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Error: React.FC<ErrorProps> = ({ onRetry, onClose }) => {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="w-full max-w-md overflow-hidden rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50 p-5">
          <div className="flex items-center space-x-3">
            <XCircle className="h-6 w-6 text-error-600" />
            <h2 className="text-lg font-semibold text-gray-900">Send failed</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-2xl leading-none text-gray-400 hover:text-gray-600"
            aria-label="Close"
          >
            &times;
          </button>
        </div>

        <div className="p-5">
          <p className="mb-6 text-center text-sm text-gray-700">
            An error occurred while sending bulk emails. Please try again.
          </p>

          <div className="flex justify-center gap-3">
            <button
              type="button"
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
              onClick={onClose}
            >
              Close
            </button>
            <button
              type="button"
              className="rounded-md bg-black px-6 py-2 text-sm font-medium text-white transition-colors hover:bg-gray-800"
              onClick={onRetry}
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const BulkEmailToVendorsModals = {
  Success,
  Error
};

export { BulkEmailToVendorsModals };
